import React, { createContext, useState, useEffect, useContext } from "react";
import AuthService from "../services/authService";
import API from "../services/api";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDefaultPasswordModal, setShowDefaultPasswordModal] =
    useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);

  // Validate session with backend
  const validateSession = async () => {
    const token = AuthService.getToken();
    if (!token) return false;

    try {
      await API.get("/auth/check-session");
      return true;
    } catch (error) {
      if (error.response?.status === 401) {
        return false;
      }
      return true;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const currentUser = AuthService.getCurrentUser();
      if (currentUser) {
        const isValid = await validateSession();
        if (isValid) {
          setUser(currentUser);
          checkPasswordModals(currentUser.id);
        } else {
          await logout();
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const checkPasswordModals = (userId) => {
    const defaultPwdSeen = localStorage.getItem(`defaultPwd_seen_${userId}`);
    const isDefaultPwd = localStorage.getItem("is_default_password") === "true";

    if (isDefaultPwd && !defaultPwdSeen) {
      setShowDefaultPasswordModal(true);
      return;
    }

    const lastChange = localStorage.getItem("last_password_change");
    const reminderSkipped = localStorage.getItem(`reminder_skip_${userId}`);
    const skipTimestamp = reminderSkipped ? parseInt(reminderSkipped) : 0;
    const sixMonthsMs = 180 * 24 * 60 * 60 * 1000;
    const now = Date.now();

    if (lastChange && !reminderSkipped) {
      const lastChangeDate = new Date(lastChange);
      const daysSinceChange =
        (now - lastChangeDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceChange >= 180) {
        setShowReminderModal(true);
      }
    } else if (reminderSkipped && now - skipTimestamp >= sixMonthsMs) {
      localStorage.removeItem(`reminder_skip_${userId}`);
      setShowReminderModal(true);
    }
  };

  // FIXED: Corrected login function
  const login = async (identifier, password) => {
    try {
      const response = await AuthService.login({ identifier, password });

      // DEBUG: Log everything
      console.log("=== AUTHCONTEXT LOGIN DEBUG ===");
      console.log("Full response:", response);
      console.log("response.data:", response.data);
      console.log("response.data.data:", response.data?.data);
      console.log("response.data.data?.user:", response.data?.data?.user);
      console.log("=================================");

      // Try different possible paths
      let userData;
      let isDefaultPwd;
      let lastPwdChange;

      // Check where the data actually is
      if (response.data && response.data.data) {
        // Structure: { status, message, data: { token, user, ... } }
        userData = response.data.data.user;
        isDefaultPwd = response.data.data.is_default_password;
        lastPwdChange = response.data.data.last_password_change;
      } else if (response.data && response.data.user) {
        // Structure: { token, user, ... }
        userData = response.data.user;
        isDefaultPwd = response.data.is_default_password;
        lastPwdChange = response.data.last_password_change;
      } else if (response.user) {
        // Structure: { user, ... }
        userData = response.user;
        isDefaultPwd = response.is_default_password;
        lastPwdChange = response.last_password_change;
      } else {
        console.error("Unknown response structure:", response);
        throw new Error("Invalid response structure from server");
      }

      console.log("Extracted userData:", userData);

      setUser(userData);

      localStorage.setItem("is_default_password", isDefaultPwd);
      if (lastPwdChange) {
        localStorage.setItem("last_password_change", lastPwdChange);
      }

      const userId = userData.id;
      const defaultPwdSeen = localStorage.getItem(`defaultPwd_seen_${userId}`);

      if (isDefaultPwd && !defaultPwdSeen) {
        setShowDefaultPasswordModal(true);
      } else if (!isDefaultPwd && lastPwdChange) {
        const lastChangeDate = new Date(lastPwdChange);
        const daysSinceChange =
          (Date.now() - lastChangeDate.getTime()) / (1000 * 60 * 60 * 24);
        if (daysSinceChange >= 180) {
          const reminderSkipped = localStorage.getItem(
            `reminder_skip_${userId}`,
          );
          if (!reminderSkipped) {
            setShowReminderModal(true);
          }
        }
      }

      return response;
    } catch (error) {
      console.error("Login error in AuthContext:", error);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const response = await AuthService.register(userData);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await API.post("/auth/logout");
    } catch (error) {
      console.error("Logout endpoint error:", error);
    }

    AuthService.logout();
    setUser(null);
    setShowDefaultPasswordModal(false);
    setShowReminderModal(false);
    localStorage.removeItem("is_default_password");
    localStorage.removeItem("last_password_change");

    const allKeys = Object.keys(localStorage);
    allKeys.forEach((key) => {
      if (
        key.startsWith("defaultPwd_seen_") ||
        key.startsWith("reminder_skip_")
      ) {
        localStorage.removeItem(key);
      }
    });
  };

  const onPasswordChangeSuccess = (userId) => {
    const now = new Date().toISOString().split("T")[0];
    localStorage.setItem("last_password_change", now);
    localStorage.setItem("is_default_password", "false");

    setShowDefaultPasswordModal(false);
    setShowReminderModal(false);

    localStorage.removeItem(`defaultPwd_seen_${userId}`);
    localStorage.removeItem(`reminder_skip_${userId}`);
  };

  const dismissDefaultPasswordModal = (userId) => {
    localStorage.setItem(`defaultPwd_seen_${userId}`, "true");
    setShowDefaultPasswordModal(false);
  };

  const dismissReminderModal = (userId) => {
    localStorage.setItem(`reminder_skip_${userId}`, Date.now().toString());
    setShowReminderModal(false);
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    showDefaultPasswordModal,
    showReminderModal,
    dismissDefaultPasswordModal,
    dismissReminderModal,
    onPasswordChangeSuccess,
    isAuthenticated: AuthService.isAuthenticated,
    isAdmin: user?.role === "admin",
    isStudent: user?.role === "student",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
